import React, { useState } from 'react'
import { ColumnDefinition, CellDefinition } from 'common/components/DataTable'
import BigSpinner from 'common/components/BigSpinner'
import { DataTable } from 'common/components/DataTable'
import { usePaginatedFetch } from 'common/hooks/usePaginatedFetch'
import Title from 'common/components/Title'
import Utils from "common/Utils"
import { Button } from 'react-bootstrap'
import API from 'common/logic/API'
import OperationWorkflowModal from 'common/components/OperationWorkflowModal'
import EditFundWorkflow from 'application/funds/EditFundWorkflow'
import AdjustFundWorkflow from 'application/funds/AdjustFundWorkflow'
import * as Icon from 'react-bootstrap-icons'
import _ from 'lodash'
import { Fund } from 'types'
import { encodeFilterTree } from 'common/logic/FilterTree'

interface Props {
    query?: Record<string, string>
    url?: string
}

function FundsList(_props: Props) {
    const [modal, setModal] = useState<{ type: string; id?: number; name?: string } | null>(null)
    const [refreshKey, setRefreshKey] = useState(0)
    const { results, error } = usePaginatedFetch<Fund>('/funds', undefined, refreshKey)

    function refreshData() { setRefreshKey(k => k + 1) }
    function hideDialogs() { setModal(null) }

    const columnDefinitions = [
        new ColumnDefinition<Fund>("Name", obj => {
            const filter = encodeFilterTree({ kind: 'leaf', type: 'FUND', parameter: String(obj.id) })
            return <><a href={`/movements?filter=${encodeURIComponent(filter)}`}>{obj.name}</a> [{obj.shortName}]</>
        }),
        new ColumnDefinition<Fund>("Amount", obj => <>{Utils.formatMoney(obj.amount, true)} {obj.currency.name}</>, "td-currency", "td-right"),
        new ColumnDefinition<Fund>("Institution", "institution", "td-size-s"),
        new ColumnDefinition<Fund>("User for new movements", obj => obj.usedForNewMovements ? "YES" : "NO", "td-size-s"),
        new ColumnDefinition<Fund>("Investment", obj => obj.isInvestment ? "YES" : "NO", "td-size-s"),
        new ColumnDefinition<Fund>("Actions", obj => <>
            <Button variant="link" title="Adjust" onClick={() => setModal({ type: "adjust", id: obj.id, name: obj.name })}><Icon.ArrowsCollapse /></Button> |
            <Button variant="link" title="Edit" onClick={() => setModal({ type: "edit", id: obj.id })}><Icon.Pencil /></Button> |
            <Button variant="link" title="Delete" onClick={() => setModal({ type: "delete", id: obj.id, name: obj.name })}><Icon.Trash /></Button>
        </>)
    ]

    let modalEl: React.ReactNode = null
    if (modal) {
        const completion = (result: unknown) => { hideDialogs(); if (result) refreshData() }
        switch (modal.type) {
            case "delete":
                modalEl = <OperationWorkflowModal
                    confirmationStep={() => `Delete fund "${modal.name}"?`}
                    executionStep={() => API.delete(`/funds/${modal.id}`)}
                    completionCallback={completion}
                />
                break
            case "create":
            case "edit":
                modalEl = <EditFundWorkflow onSuccess={completion} entryId={modal.id} />
                break
            case "adjust":
                modalEl = <AdjustFundWorkflow onSuccess={completion} entryId={modal.id!} />
                break
            default:
        }
    }

    function sumByCurrency(fundResults: Fund[]) {
        function parseCents(x: string) { return parseInt(x.replace(".", "")) }
        const nonDeleted = fundResults.filter(obj => !obj.deleted)
        const byCurrency = _.groupBy(nonDeleted, (r: Fund) => r.currency.name)
        const sums = _.mapValues(byCurrency, (list: Fund[]) => _.reduce(list, (sum: number, e: Fund) => sum + parseCents(e.amount), 0))
        return _.toPairs(sums).map((e: [string, number]) => <>
            <strong>TOTAL:</strong> {Utils.formatMoney(e[1] / 100, true)} {e[0]}
        </>
        )
    }

    return (
        <>
            <Title title="Funds" />
            {error && <p>Error!</p>}
            {results === null ? <BigSpinner /> : <>
                <div>{sumByCurrency(results)}</div>
                <Button variant="link" onClick={() => setModal({ type: "create" })}>Add new</Button>
                <DataTable
                    columns={columnDefinitions as unknown as ColumnDefinition[]}
                    rows={results.filter(obj => !obj.deleted) as unknown as Record<string, unknown>[]}
                />
            </>}
            {modalEl}
        </>
    )
}

export default FundsList
